import React, { useEffect, useMemo } from 'react';

import { BudgetLineSelector, DepartmentSelector } from './Selectors';

import ToggleFormField from 'CommonComponents/ToggleFormField';

import useQuery from 'Hooks/useQuery';

const DEPARTMENT_LEVEL = 'department-level';

const isCostCentreType = (type) => /costcenter$/i.test(type || '');

// Ids are only unique per line type (a workforce row and an expense can share one), so options are keyed by both.
const lineKey = (type, id) => `${type}::${id}`;

/**
 * Department / cost centre, "Attach to" and include-in-AFC fields shared by the risk and opportunity forms.
 *
 * `departmentAside` renders beside the department selector.
 */
export default function BudgetAttachmentFields({
    departmentsOptions,
    fetchBudgetLinesUrl,
    values,
    setFieldValue,
    errors,
    touched,
    lockDepartment = false,
    disabled = false,
    afcField,
    afcLabel,
    showAfcWhenCentral = false,
    departmentAside = null,
}) {
    const budgetLinesRequest = useQuery({
        enabled: false,
        queryFn: ({ budget_department_id, budget_department_type }) =>
            axios.get(fetchBudgetLinesUrl, { params: { budget_department_type, budget_department_id } }),
    });

    useEffect(() => {
        if (!values.budget_department_id || !values.budget_department_type) {
            return;
        }

        budgetLinesRequest
            .refetch({
                budget_department_id: values.budget_department_id,
                budget_department_type: values.budget_department_type,
            })
            .catch(() => {});
    }, [values.budget_department_id, values.budget_department_type]);

    const departmentName = useMemo(() => {
        const options = (departmentsOptions || []).flatMap((option) => option.options || [option]);

        return options.find((option) => option.value === values.budget_department_key)?.label || '';
    }, [departmentsOptions, values.budget_department_key]);

    const attachToOptions = useMemo(
        () => [
            {
                label: '',
                options: [
                    {
                        value: DEPARTMENT_LEVEL,
                        label: isCostCentreType(values.budget_department_type)
                            ? $t('{{name}} (cost centre level)', { name: departmentName })
                            : $t('{{name}} (department level)', { name: departmentName }),
                    },
                ],
            },
            ...(budgetLinesRequest.data || []).map((group) => ({
                ...group,
                options: group.options.map((option) => ({ ...option, value: lineKey(option.type, option.value), lineId: option.value })),
            })),
        ],
        [budgetLinesRequest.data, departmentName, values.budget_department_type],
    );

    const showAfc = !!values.budget_department_key || showAfcWhenCentral;

    return (
        <>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                    <DepartmentSelector
                        departmentsOptions={departmentsOptions}
                        disabled={lockDepartment || disabled}
                        values={values}
                        onChange={(department) => {
                            setFieldValue('budget_department_key', department?.value || '');
                            setFieldValue('budget_department_id', department?.id || '');
                            setFieldValue('budget_department_type', department?.type || '');
                            setFieldValue('budget_line_id', null);
                            setFieldValue('budget_line_type', null);

                            if (!department && !showAfcWhenCentral) {
                                setFieldValue(afcField, false);
                            }
                        }}
                        errors={errors.budget_department_key}
                        touched={touched.budget_department_key}
                    />
                </div>
                {departmentAside}
            </div>

            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                {!!values.budget_department_key && (
                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                        <BudgetLineSelector
                            budgetLines={attachToOptions}
                            values={values}
                            value={
                                values.budget_line_id && values.budget_line_type ? lineKey(values.budget_line_type, values.budget_line_id) : DEPARTMENT_LEVEL
                            }
                            onChange={(option) => {
                                const isDepartmentLevel = !option || option.value === DEPARTMENT_LEVEL;

                                setFieldValue('budget_line_id', isDepartmentLevel ? null : option.lineId);
                                setFieldValue('budget_line_type', isDepartmentLevel ? null : option.type);
                            }}
                            disabled={budgetLinesRequest.loading || disabled}
                            errors={errors.budget_line_id}
                            touched={touched.budget_line_id}
                        />
                    </div>
                )}
                {showAfc && (
                    <ToggleFormField
                        label={afcLabel}
                        type="checkbox"
                        name={afcField}
                        id={afcField}
                        checked={!!values[afcField]}
                        onChange={() => setFieldValue(afcField, !values[afcField])}
                    />
                )}
            </div>
        </>
    );
}
